import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloseIcon from '@mui/icons-material/Close';
import {
    Box,
    Fab,
    IconButton,
    Tooltip,
    Typography,
    useTheme,
    Zoom
} from '@mui/material';

const ONBOARDING_AUTO_HIDE_MS = 2800;
const ONBOARDING_ZOOM_TIMEOUT_MS = 140;

const FloatingMenu = () => {
    // Visibility states
    const [showUp, setShowUp] = useState(false);
    const [showDown, setShowDown] = useState(true);
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [inFooter, setInFooter] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();

    const hideArrows = location.pathname.startsWith('/login') || location.pathname.startsWith('/portal-cliente');

    const scrollAnimRef = useRef({ rafId: null });

    const getNavMode = useCallback(() => {
        const isReaderRoute =
            location.pathname.startsWith('/news/') ||
            location.pathname.startsWith('/governanca/');
        if (isReaderRoute) return 'content';

        // If the page has multiple snap sections, arrows should navigate sections.
        // If it's a single-section route (news article, admin, etc), arrows should scroll content.
        const sectionCount = document.querySelectorAll('section.section').length;
        return sectionCount <= 1 ? 'content' : 'sections';
    }, [location.pathname]);

    const getActiveSnapTarget = useCallback(() => {
        const targets = Array.from(document.querySelectorAll('.section, .footer-wrapper'));
        if (targets.length === 0) return null;

        // The snap point is roughly under the header.
        const snapPoint = 100;
        let best = targets[0];
        let bestDist = Infinity;

        for (const el of targets) {
            const rect = el.getBoundingClientRect();
            const dist = Math.abs(rect.top - snapPoint);
            if (dist < bestDist) {
                bestDist = dist;
                best = el;
            }
        }
        return best;
    }, []);

    const getScrollContainer = useCallback(() => {
        const scroller = document.querySelector('.scroller');

        // Helper: pick only truly scrollable containers, otherwise fall back to window.
        const isScrollableEl = (el) => {
            if (!el || el === window) return false;
            const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight);
            return maxScroll > 10;
        };

        if (getNavMode() === 'content') {
            const active = getActiveSnapTarget();
            const body = active?.querySelector?.('.section-body');

            // Prefer inner reader container when it exists and is scrollable.
            if (isScrollableEl(body)) return body;

            // Some routes use the outer `.scroller` for scrolling, others scroll the window.
            if (isScrollableEl(scroller)) return scroller;

            return window;
        }

        // Section-snap mode: `.scroller` is the primary container.
        return scroller || window;
    }, [getActiveSnapTarget, getNavMode]);

    const getScrollState = useCallback(() => {
        const container = getScrollContainer();
        if (container === window) {
            const scrollTop = window.scrollY;
            const scrollHeight = document.documentElement.scrollHeight;
            const clientHeight = window.innerHeight;
            const maxScroll = Math.max(0, scrollHeight - clientHeight);
            return { scrollTop, maxScroll };
        }

        const scrollTop = container.scrollTop;
        const scrollHeight = container.scrollHeight;
        const clientHeight = container.clientHeight;
        const maxScroll = Math.max(0, scrollHeight - clientHeight);
        return { scrollTop, maxScroll };
    }, [getScrollContainer]);

    const handleScrollVisibility = useCallback(() => {
        const { scrollTop, maxScroll } = getScrollState();
        const activeTarget = getActiveSnapTarget();
        const isFooter = !!activeTarget && (activeTarget.id === 'footer' || activeTarget.classList?.contains('footer-wrapper'));
        // Reading UX: "back to top" should be available even for small scroll distances.
        setShowUp(scrollTop > 5);
        setShowDown(scrollTop < maxScroll - 50);
        setInFooter(isFooter);

        // If user starts navigating, remove the onboarding balloon.
        if (scrollTop > 200) setShowOnboarding(false);
    }, [getScrollState, getActiveSnapTarget]);

    const scrollToTop = useCallback(() => {
        const container = getScrollContainer();
        if (!container) return;

        if (scrollAnimRef.current.rafId) {
            cancelAnimationFrame(scrollAnimRef.current.rafId);
            scrollAnimRef.current.rafId = null;
        }

        if (container === window) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        // If we can't find an inner scroll container, jump the main scroller.
        if (container.classList?.contains('scroller')) {
            container.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        container.scrollTo({ top: 0, behavior: 'smooth' });
    }, [getScrollContainer]);

    const scrollToSection = (direction) => {
        // Dynamic detection of targets: Sections and Footer
        const targets = Array.from(document.querySelectorAll('.section, .footer-wrapper, #footer'));
        if (targets.length === 0) return;

        // Find container
        const scroller = document.querySelector('.scroller');
        if (!scroller) return;

        // Determine current active section based on proximity to top
        let currentIndex = 0;
        let minDistance = Infinity;

        targets.forEach((el, index) => {
            const rect = el.getBoundingClientRect();
            // We verify distance to the top of the viewport/scroller
            const val = Math.abs(rect.top);
            if (val < minDistance) {
                minDistance = val;
                currentIndex = index;
            }
        });

        // Calculate Target Index
        let targetIndex = currentIndex;

        if (direction === 'up') {
            if (targetIndex > 0) targetIndex--;
        } else {
            if (targetIndex < targets.length - 1) targetIndex++;
        }

        // Scroll
        const targetElement = targets[targetIndex];
        if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const scrollContent = (direction) => {
        const container = getScrollContainer();
        // Fallback: if we can't find an inner scroll container, behave like section nav.
        if (!container || container === window || container.classList?.contains('scroller')) {
            scrollToSection(direction);
            return;
        }

        // Reading UX: smaller step prevents the user from losing their place.
        // Use a fraction of viewport height with sane bounds.
        const delta = Math.min(
            260,
            Math.max(120, Math.round(container.clientHeight * 0.22))
        );
        const startTop = container.scrollTop;
        const maxTop = Math.max(0, container.scrollHeight - container.clientHeight);
        const targetTop = Math.min(maxTop, Math.max(0, startTop + (direction === 'down' ? delta : -delta)));

        if (scrollAnimRef.current.rafId) {
            cancelAnimationFrame(scrollAnimRef.current.rafId);
            scrollAnimRef.current.rafId = null;
        }

        const durationMs = 220;
        const t0 = performance.now();
        const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

        const tick = (now) => {
            const t = Math.min(1, (now - t0) / durationMs);
            const eased = easeInOutCubic(t);
            container.scrollTop = startTop + (targetTop - startTop) * eased;
            if (t < 1) {
                scrollAnimRef.current.rafId = requestAnimationFrame(tick);
            } else {
                scrollAnimRef.current.rafId = null;
            }
        };

        scrollAnimRef.current.rafId = requestAnimationFrame(tick);
    };

    const handleArrow = (direction) => {
        if (getNavMode() === 'content') {
            // Reading mode: arrows are step-scroll affordances (not "go to top").
            scrollContent(direction);
            return;
        }
        scrollToSection(direction);
    };

    useEffect(() => {
        // Attach to the scroll container when it exists; otherwise fallback to window scroll.
        // Some routes render the `.scroller` slightly after mount, so we poll briefly and swap.
        let target = null;
        let intervalId = null;

        const setTarget = (nextTarget) => {
            if (target === nextTarget) return;
            if (target) target.removeEventListener('scroll', handleScrollVisibility);
            target = nextTarget;
            target.addEventListener('scroll', handleScrollVisibility, { passive: true });
            handleScrollVisibility();
        };

        setTarget(getScrollContainer());

        if (target === window) {
            intervalId = setInterval(() => {
                const scroller = document.querySelector('.scroller');
                if (scroller) {
                    // Only swap to `.scroller` if it's actually scrollable; otherwise keep window.
                    setTarget(getScrollContainer());
                    clearInterval(intervalId);
                    intervalId = null;
                }
            }, 100);
        }

        return () => {
            if (intervalId) clearInterval(intervalId);
            if (target) target.removeEventListener('scroll', handleScrollVisibility);
        };
    }, [handleScrollVisibility, getScrollContainer]);

    useEffect(() => {
        // Route changes may keep FloatingMenu mounted; recompute visibility.
        // Also re-check after layout/media load, otherwise maxScroll may be read as 0.
        const raf = requestAnimationFrame(handleScrollVisibility);
        const t1 = setTimeout(handleScrollVisibility, 250);
        const t2 = setTimeout(handleScrollVisibility, 1200);
        return () => {
            cancelAnimationFrame(raf);
            clearTimeout(t1);
            clearTimeout(t2);
        };
    }, [location.pathname, handleScrollVisibility]);

    useEffect(() => {
        // In single-section pages, the inner `.section-body` scrolls, not the outer `.scroller`.
        // Attach a listener so the arrows show/hide correctly while reading.
        let inner = null;
        let intervalId = null;

        const attach = () => {
            if (getNavMode() !== 'content') return false;
            const next = getScrollContainer();
            if (!next || next === window || next.classList?.contains('scroller')) return false;
            if (inner === next) return true;

            if (inner) inner.removeEventListener('scroll', handleScrollVisibility);
            inner = next;
            inner.addEventListener('scroll', handleScrollVisibility, { passive: true });
            handleScrollVisibility();
            return true;
        };

        if (!attach()) {
            intervalId = setInterval(() => {
                if (attach()) {
                    clearInterval(intervalId);
                    intervalId = null;
                }
            }, 150);
        }

        return () => {
            if (intervalId) clearInterval(intervalId);
            if (inner) inner.removeEventListener('scroll', handleScrollVisibility);
        };
    }, [location.pathname, getNavMode, getScrollContainer, handleScrollVisibility]);

    useEffect(() => {
        // Reading UX: allow keyboard ArrowUp/ArrowDown to scroll the article body.
        // Do not hijack arrow keys globally; scope to news reading pages and ignore form fields.
        const isNewsReading = location.pathname.startsWith('/news/');
        if (!isNewsReading) return;

        const isEditableTarget = (target) => {
            if (!target) return false;
            if (target.isContentEditable) return true;
            const tag = (target.tagName || '').toLowerCase();
            return tag === 'input' || tag === 'textarea' || tag === 'select';
        };

        const scrollStep = (direction) => {
            const container = getScrollContainer();
            if (!container || container === window || container.classList?.contains('scroller')) {
                // Fall back to existing section navigation logic if inner scroll isn't present.
                scrollToSection(direction);
                return;
            }

            const delta = Math.min(
                260,
                Math.max(120, Math.round(container.clientHeight * 0.22))
            );
            const startTop = container.scrollTop;
            const maxTop = Math.max(0, container.scrollHeight - container.clientHeight);
            const targetTop = Math.min(
                maxTop,
                Math.max(0, startTop + (direction === 'down' ? delta : -delta))
            );

            if (scrollAnimRef.current.rafId) {
                cancelAnimationFrame(scrollAnimRef.current.rafId);
                scrollAnimRef.current.rafId = null;
            }

            const durationMs = 220;
            const t0 = performance.now();
            const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

            const tick = (now) => {
                const t = Math.min(1, (now - t0) / durationMs);
                const eased = easeInOutCubic(t);
                container.scrollTop = startTop + (targetTop - startTop) * eased;
                if (t < 1) {
                    scrollAnimRef.current.rafId = requestAnimationFrame(tick);
                } else {
                    scrollAnimRef.current.rafId = null;
                    handleScrollVisibility();
                }
            };

            scrollAnimRef.current.rafId = requestAnimationFrame(tick);
        };

        const onKeyDown = (e) => {
            if (!e) return;
            if (e.defaultPrevented) return;
            if (e.altKey || e.ctrlKey || e.metaKey) return;
            if (isEditableTarget(e.target)) return;

            // Only override in reading mode (single section).
            if (getNavMode() !== 'content') return;

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                scrollStep('down');
                return;
            }

            if (e.key === 'ArrowUp') {
                e.preventDefault();
                scrollStep('up');
                return;
            }

            // Dedicated shortcut for "go to top" of the article.
            if (e.key === 'Home') {
                e.preventDefault();
                scrollToTop();
            }
        };

        window.addEventListener('keydown', onKeyDown, { passive: false });
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [location.pathname, getNavMode, getScrollContainer, handleScrollVisibility, scrollToTop]);

    // Keep menu mounted across routes; state is reset on route change only if needed.
    useEffect(() => {
        // Ensure visibility recalculates after navigation.
        // (Some routes change the set of .section elements and scroll position.)
        handleScrollVisibility();

        // Show onboarding whenever user lands at the top/hero of a page.
        // This matches the "always show on open" expectation without popping up mid-scroll.
        const { scrollTop } = getScrollState();
        if (scrollTop <= 120) {
            setShowOnboarding(true);
            const t = setTimeout(() => setShowOnboarding(false), ONBOARDING_AUTO_HIDE_MS);
            return () => clearTimeout(t);
        }
    }, [location.pathname, getScrollState, handleScrollVisibility]);

    const homeStyle = {
        width: { xs: 50, md: 60 },
        height: { xs: 50, md: 60 },
        backgroundColor: 'common.white',
        color: 'common.black',
        boxShadow: theme.shadows[4],
        transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
        '&:hover': {
            transform: 'scale(1.1)',
            backgroundColor: 'primary.main',
            color: 'common.white',
            boxShadow: theme.shadows[8],
        }
    };

    const arrowStyle = {
        width: { xs: 50, md: 60 },
        height: { xs: 50, md: 60 },
        backgroundColor: 'common.white', // Default White
        color: 'common.black', // Black Icon (Corrected)
        boxShadow: theme.shadows[4],
        transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
        '&:hover': {
            transform: 'scale(1.1)',
            backgroundColor: '#CC0000', // Hover Red (R5 Brand)
            color: 'common.white', // White Icon
            boxShadow: theme.shadows[8],
        }
    };

    return (
        <Zoom in>
            <Box
                data-ui="floating-menu"
                sx={{
                    position: 'fixed',
                    right: { xs: 8, md: 10 },
                    // Footer: move the menu a bit up so it doesn't sit over the footer meta area.
                    top: { xs: 'auto', md: inFooter ? '38%' : '50%' },
                    bottom: { xs: 30, md: 'auto' },
                    transform: { xs: 'none', md: 'translateY(-50%)' },
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.875,
                    zIndex: 9999,
                    alignItems: 'center' // Center align for tooltip containment
                }}
            >
                {/* ONBOARDING TOOLTIP */}
                <Zoom in={showOnboarding} timeout={ONBOARDING_ZOOM_TIMEOUT_MS}>
                    <Box
                        onClick={() => setShowOnboarding(false)}
                        sx={{
                            position: 'absolute',
                            right: 'calc(100% + 20px)',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: '240px',
	                            bgcolor: '#FFFFFF',
	                            color: '#000000',
	                            p: 2.5,
	                            borderRadius: '18px',
	                            borderLeft: '4px solid #CC0000',
	                            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
	                            cursor: 'pointer',
	                            pointerEvents: 'auto'
	                        }}
	                    >
	                        {/* Arrow (Diamond) */}
	                        <Box
	                            sx={{
	                                position: 'absolute',
	                                right: '-7px',
	                                top: '50%',
	                                width: '14px',
	                                height: '14px',
	                                marginTop: '-7px',
	                                bgcolor: '#FFFFFF',
	                                borderRadius: '4px',
	                                transform: 'rotate(45deg)',
	                            }}
	                        />

                        <IconButton
                            aria-label="Fechar"
                            title="Fechar"
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowOnboarding(false);
                            }}
                            sx={{
                                position: 'absolute',
                                top: 6,
                                right: 6,
                                width: 28,
                                height: 28,
	                                color: '#CC0000',
                                opacity: 0.75,
                                transition: 'opacity 0.15s ease, background-color 0.15s ease',
                                '&:hover': {
                                    opacity: 1,
	                                    backgroundColor: 'rgba(204, 0, 0, 0.08)',
                                },
                            }}
                        >
                            <CloseIcon sx={{ fontSize: 18 }} />
                        </IconButton>

	                        <Typography variant="subtitle2" sx={{
	                            fontFamily: '"JetBrains Mono", monospace',
	                            fontWeight: 800,
		                            color: '#CC0000',
	                            mb: 1,
	                            fontSize: '0.85rem',
	                            letterSpacing: '0.5px'
	                        }}>
	                            {'/// NAVEGAÇÃO'}
	                        </Typography>
                        <Typography variant="body2" sx={{
                            fontSize: '0.85rem',
                            lineHeight: 1.5,
                            fontWeight: 500,
                            color: '#0C0C0C'
                        }}>
                            Utilize o menu lateral para navegar entre as seções do portal.
                        </Typography>
                    </Box>
                </Zoom>

                {!hideArrows && (
                    <Tooltip
                        title={getNavMode() === 'content' ? 'Subir' : (showUp ? 'Subir' : 'No topo')}
                        placement="left"
                    >
                        <span>
                            <Fab
                                size="large"
                                disabled={getNavMode() !== 'content' && !showUp}
                                onClick={() => handleArrow('up')}
                                data-ui="btn-scroll-up"
                                sx={{
                                    ...arrowStyle,
                                    '&.Mui-disabled': {
                                        opacity: 0.35,
                                        color: 'common.black',
                                        backgroundColor: 'common.white',
                                        boxShadow: theme.shadows[1],
                                    }
                                }}
                                aria-label="Subir"
                            >
                                <ArrowUpwardIcon />
                            </Fab>
                        </span>
                    </Tooltip>
                )}

                {/* HOME / RESET */}
                {(() => {
                    const isReaderLikeView =
                        location.pathname.startsWith('/news/') ||
                        location.pathname.startsWith('/governanca/');
                    return (
                        <Tooltip title={isReaderLikeView ? 'Topo da leitura' : 'Início'} placement="left">
                            <Fab
                                size="large"
                                onClick={() => {
                                    if (isReaderLikeView) {
                                        scrollToTop();
                                        return;
                                    }

                                    const hero = document.getElementById('hero');
                                    if (hero) {
                                        hero.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    } else {
                                        const scroller = document.querySelector('.scroller');
                                        if (scroller) {
                                            scroller.scrollTo({ top: 0, behavior: 'smooth' });
                                        } else {
                                            window.scrollTo({ top: 0, behavior: 'smooth' });
                                        }
                                    }
                                    navigate('/');
                                }}
                                data-ui="btn-home"
                                sx={homeStyle}
                                aria-label={isReaderLikeView ? 'Topo da leitura' : 'Início'}
                            >
                                {isReaderLikeView ? <VerticalAlignTopIcon /> : <HomeIcon />}
                            </Fab>
                        </Tooltip>
                    );
                })()}

                {!hideArrows && (
                    <Tooltip
                        title={showDown ? 'Descer' : 'No fim'}
                        placement="left"
                    >
                        <span>
                            <Fab
                                size="large"
                                disabled={!showDown}
                                onClick={() => handleArrow('down')}
                                data-ui="btn-scroll-down"
                                sx={{
                                    ...arrowStyle,
                                    '&.Mui-disabled': {
                                        opacity: 0.35,
                                        color: 'common.black',
                                        backgroundColor: 'common.white',
                                        boxShadow: theme.shadows[1],
                                    }
                                }}
                                aria-label="Descer"
                            >
                                <ArrowDownwardIcon />
                            </Fab>
                        </span>
                    </Tooltip>
                )}
            </Box>
        </Zoom>
    );
};

export default FloatingMenu;
